/**
 * Onboarding Service — v2
 * Handles onboarding API calls with updated payload structure
 */

import http from './http'
import type { OnboardingFormData } from '../types/onboarding'

export interface OnboardingResponse {
  data: any[]
  message?: string
}

export interface OnboardingStatusResponse {
  completed: boolean
}

class OnboardingService {
  /**
   * Save onboarding answers
   * POST /api/onboarding
   */
  async saveOnboardingData(data: OnboardingFormData): Promise<OnboardingResponse> {
    console.log('[OnboardingService.saveOnboardingData] Saving onboarding data')

    const payload = {
      public_figures: data.publicFigures.filter(s => s.trim()).map(s => s.trim()),
      brands: data.brands.filter(s => s.trim()).map(s => s.trim()),
      main_goals: data.mainGoals,
      content_types: data.contentTypes,
      content_associations: data.contentAssociations,
      topic_focus: data.topicFocus
    }

    console.log('[OnboardingService.saveOnboardingData] Payload:', payload)

    try {
      const response = await http.post<OnboardingResponse>('/api/onboarding', payload, {
        timeout: 180000 // 3 minutes
      })
      console.log('[OnboardingService.saveOnboardingData] Success:', response.data)
      return response.data
    } catch (error: any) {
      console.error('[OnboardingService.saveOnboardingData] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Check if user has completed onboarding
   * GET /api/user-onboarding-status
   */
  async getOnboardingStatus(): Promise<OnboardingStatusResponse> {
    console.log('[OnboardingService.getOnboardingStatus] Checking status')
    try {
      const response = await http.get<OnboardingStatusResponse>('/api/user-onboarding-status')
      console.log('[OnboardingService.getOnboardingStatus] Status:', response.data)
      return response.data
    } catch (error: any) {
      console.error('[OnboardingService.getOnboardingStatus] Failed:', error)
      return { completed: false }
    }
  }

  /**
   * Trigger profile generation after onboarding
   * POST /api/generate-profile
   */
  async generateProfile(): Promise<{ generated: boolean }> {
    console.log('[OnboardingService.generateProfile] Triggering profile generation')
    try {
      const response = await http.post<{ generated: boolean }>('/api/generate-profile')
      console.log('[OnboardingService.generateProfile] Result:', response.data)
      return response.data
    } catch (error: any) {
      console.error('[OnboardingService.generateProfile] Failed:', error)
      return { generated: false }
    }
  }

  private handleError(error: any): Error {
    if (error.response?.data?.detail) {
      return new Error(error.response.data.detail)
    }
    if (error.response?.data?.message) {
      return new Error(error.response.data.message)
    }
    return new Error(error.message || 'Onboarding service error')
  }
}

export default new OnboardingService()
